/**
 * Konkurranser-fane komponent
 */

import React, { useState } from 'react';
import { Trophy, Sword, Users, MessageCircle, ArrowLeft, Send, Trash2, X, ChevronRight, RefreshCw, HelpCircle, Medal } from 'lucide-react';
import { COMPETITION_MODES } from '../constants/config';
import { useCompetitionChat } from '../hooks/useFirestore';
import { sendChatMessage, deleteCompetition, deleteChatMessage, leaveCompetition, markCompetitionAsSeen } from '../services/firestore';
import { calculateTugStats } from '../utils/calculations';
import { checkAndArchiveCompetition } from '../utils/archiveCompetition';

export const CompetitionsTab = ({ competitions, userId, userProfile, leaderboard, initialSelectedCompId, onCompetitionChange, onSync, syncing }) => {
  const [selectedCompId, setSelectedCompId] = useState(initialSelectedCompId || null);
  const [chatMessage, setChatMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showHistorical, setShowHistorical] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Helper function to check if competition is finished
  const isCompetitionFinished = (comp) => {
    // Hvis allerede arkivert, er den ferdig (uansett live data)
    if (comp.archived) return true;
    
    if (comp.mode === COMPETITION_MODES.LONGEST_DISTANCE && comp.endDate) {
      return new Date(comp.endDate) < new Date();
    }
    
    // Tug of war: Check if one team has reached the goal (kun hvis ikke arkivert)
    if (comp.mode === COMPETITION_MODES.TUG_OF_WAR && comp.goal && !comp.archived) {
      const stats = calculateTugStats(comp, leaderboard);
      const difference = stats.teamA - stats.teamB;
      return Math.abs(difference) >= comp.goal;
    }
    
    // First to goal: Check if anyone has reached the goal (kun hvis ikke arkivert)
    if (comp.mode === COMPETITION_MODES.FIRST_TO_GOAL && comp.goal && !comp.archived) {
      const hasWinner = comp.participants.some(id => {
        const participant = leaderboard.find(l => l.id === id);
        if (!participant) return false;
        const km = participant.competitionKm?.[comp.id] || 0;
        return km >= comp.goal;
      });
      return hasWinner;
    }
    
    return false;
  };

  // Update selected competition when initialSelectedCompId changes
  React.useEffect(() => {
    setSelectedCompId(initialSelectedCompId || null);
  }, [initialSelectedCompId]);

  // Mark competition as seen when viewing a finished competition
  React.useEffect(() => {
    const markAsSeen = async () => {
      if (selectedCompId && userId) {
        const comp = competitions.find(c => c.id === selectedCompId);
        if (comp && isCompetitionFinished(comp) && !comp.seenBy?.[userId]) {
          await markCompetitionAsSeen(selectedCompId, userId);
        }
      }
    };
    markAsSeen();
  }, [selectedCompId, userId, competitions]);

  // Auto-archive finished competitions
  React.useEffect(() => {
    const archiveFinished = async () => {
      for (const comp of competitions) {
        if (isCompetitionFinished(comp) && !comp.archived) {
          await checkAndArchiveCompetition(comp, leaderboard);
        }
      }
    };
    archiveFinished();
  }, [competitions, leaderboard]);

  // Notify parent when competition changes
  const handleCompetitionSelect = (id) => {
    setSelectedCompId(id);
    if (onCompetitionChange) {
      onCompetitionChange(id);
    }
  };

  const handleBackToList = () => {
    setSelectedCompId(null);
    if (onCompetitionChange) {
      onCompetitionChange(null);
    }
  };

  console.log('CompetitionsTab render:', { 
    competitions: competitions?.length, 
    userId, 
    userProfile,
    leaderboard: leaderboard?.length
  });

  const selectedComp = competitions.find(c => c.id === selectedCompId);
  const messages = useCompetitionChat(selectedCompId);

  const handleDeleteCompetition = async () => {
    if (!selectedCompId) return;
    
    try {
      await deleteCompetition(selectedCompId);
      setShowDeleteConfirm(false);
      handleBackToList();
    } catch (error) {
      console.error('Error deleting competition:', error);
      alert('Kunne ikke slette konkurranse: ' + error.message);
    }
  };

  const handleLeaveCompetition = async () => {
    if (!selectedCompId) return;
    
    try {
      await leaveCompetition(selectedCompId, userId);
      setShowLeaveConfirm(false);
      handleBackToList();
    } catch (error) {
      console.error('Error leaving competition:', error);
      alert('Kunne ikke forlate konkurranse: ' + error.message);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!selectedCompId) return;
    
    try {
      await deleteChatMessage(selectedCompId, messageId);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Kunne ikke slette melding: ' + error.message);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatMessage.trim() || !selectedCompId) return;

    await sendChatMessage(selectedCompId, {
      text: chatMessage.trim(),
      senderId: userId,
      senderName: userProfile.name || 'Anonym',
      avatar: userProfile.avatar || '🏃‍♂️',
      timestamp: new Date().toISOString()
    });

    setChatMessage('');
  };

  // List view
  if (!selectedComp) {
    // Separate active, finished unseen, and historical competitions
    const now = new Date();
    const activeCompetitions = competitions.filter(comp => {
      return !isCompetitionFinished(comp);
    });
    
    const finishedUnseenCompetitions = competitions.filter(comp => {
      const isFinished = isCompetitionFinished(comp);
      const isSeen = comp.seenBy?.[userId];
      return isFinished && !isSeen && comp.participants.includes(userId);
    });
    
    const historicalCompetitions = competitions.filter(comp => {
      const isFinished = isCompetitionFinished(comp);
      const isSeen = comp.seenBy?.[userId];
      return isFinished && isSeen;
    });

    return (
      <div className="space-y-4">
        <h2 className="font-black text-[10px] uppercase tracking-[0.15em] text-slate-400 px-1">
          Aktive Konkurranser
        </h2>

        <div className="space-y-3">
          {activeCompetitions.map(comp => {
            const isParticipant = comp.participants.includes(userId);
            const icon = comp.mode === COMPETITION_MODES.TUG_OF_WAR ? Sword : Trophy;
            const Icon = icon;

            return (
              <div
                key={comp.id}
                onClick={() => handleCompetitionSelect(comp.id)}
                className="bg-white p-4 rounded-2xl border border-slate-100 flex items-center justify-between cursor-pointer hover:border-orange-400 transition-all shadow-sm group"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 ${
                    comp.mode === COMPETITION_MODES.TUG_OF_WAR ? 'bg-red-50' : 'bg-orange-50'
                  } rounded-xl flex items-center justify-center shadow-inner`}>
                    <Icon className={`${
                      comp.mode === COMPETITION_MODES.TUG_OF_WAR ? 'text-red-500' : 'text-orange-600'
                    } w-6 h-6`} />
                  </div>
                  <div>
                    <p className="font-black text-sm text-slate-800 tracking-tight">
                      {comp.title}
                    </p>
                    <div className="flex items-center gap-2">
                      <p className="text-[9px] font-black text-orange-400 uppercase tracking-tighter">
                        #{comp.code}
                      </p>
                      <span className="text-slate-300">•</span>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3 text-slate-400" />
                        <span className="text-[9px] font-bold text-slate-400">
                          {comp.participants.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                {isParticipant && (
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                )}
              </div>
            );
          })}

          {activeCompetitions.length === 0 && (
            <div className="text-center py-12">
              <Trophy className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-400 text-sm font-bold">
                Ingen aktive konkurranser
              </p>
            </div>
          )}
        </div>

        {/* Finished Unseen Competitions */}
        {finishedUnseenCompetitions.length > 0 && (
          <div className="space-y-3 mt-6">
            <h2 className="font-black text-[10px] uppercase tracking-[0.15em] text-slate-400 px-1">
              Avsluttede Konkurranser
            </h2>
            
            <div className="space-y-3">
              {finishedUnseenCompetitions.map(comp => {
                const icon = comp.mode === COMPETITION_MODES.TUG_OF_WAR ? Sword : Trophy;
                const Icon = icon;

                return (
                  <div
                    key={comp.id}
                    onClick={() => handleCompetitionSelect(comp.id)}
                    className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 rounded-2xl border-2 border-orange-200 flex items-center justify-between cursor-pointer hover:border-orange-400 transition-all shadow-md group relative overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-20 h-20 bg-orange-200/30 rounded-full -mr-10 -mt-10"></div>
                    <div className="flex items-center gap-3 relative z-10">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-md">
                        <Icon className="text-orange-600 w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-black text-sm text-slate-800 tracking-tight">
                          {comp.title}
                        </p>
                        <p className="text-[9px] font-black text-orange-600 uppercase tracking-tighter">
                          Konkurranse avsluttet!
                        </p>
                      </div>
                    </div>
                    <HelpCircle className="w-8 h-8 text-orange-500 animate-pulse" />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Historical Competitions Section */}
        {historicalCompetitions.length > 0 && (
          <div className="space-y-3 mt-6">
            <button
              onClick={() => setShowHistorical(!showHistorical)}
              className="w-full flex items-center justify-between p-4 bg-slate-50 rounded-2xl hover:bg-slate-100 transition-colors"
            >
              <h2 className="font-black text-[10px] uppercase tracking-[0.15em] text-slate-400">
                Historisk ({historicalCompetitions.length})
              </h2>
              <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showHistorical ? 'rotate-90' : ''}`} />
            </button>

            {showHistorical && (
              <div className="space-y-3">
                {historicalCompetitions.map(comp => {
                  const isParticipant = comp.participants.includes(userId);
                  const icon = comp.mode === COMPETITION_MODES.TUG_OF_WAR ? Sword : Trophy;
                  const Icon = icon;

                  // Beregn brukerens plassering og km
                  let userPlace = null;
                  let userKm = 0;
                  
                  if (isParticipant) {
                    const participants = comp.participants
                      .map(id => {
                        const participant = leaderboard.find(l => l.id === id);
                        if (!participant) return null;
                        const displayKm = participant.competitionKm?.[comp.id] || 0;
                        return { id, displayKm };
                      })
                      .filter(Boolean)
                      .sort((a, b) => b.displayKm - a.displayKm);
                    
                    const userIndex = participants.findIndex(p => p.id === userId);
                    if (userIndex !== -1) {
                      userPlace = userIndex + 1;
                      userKm = participants[userIndex].displayKm;
                    }
                  }

                  // Medal colors
                  const getMedalColor = (place) => {
                    if (place === 1) return 'text-yellow-500';
                    if (place === 2) return 'text-slate-400';
                    if (place === 3) return 'text-orange-600';
                    return 'text-slate-300';
                  };

                  const getBgColor = (place) => {
                    if (place === 1) return 'bg-yellow-50 border-yellow-200';
                    if (place === 2) return 'bg-slate-50 border-slate-200';
                    if (place === 3) return 'bg-orange-50 border-orange-200';
                    return 'bg-white border-slate-100';
                  };

                  return (
                    <div
                      key={comp.id}
                      onClick={() => handleCompetitionSelect(comp.id)}
                      className={`p-4 rounded-2xl border-2 flex items-center justify-between cursor-pointer hover:shadow-md transition-all group ${
                        isParticipant && userPlace <= 3 ? getBgColor(userPlace) : 'bg-white border-slate-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 ${
                          comp.mode === COMPETITION_MODES.TUG_OF_WAR ? 'bg-red-50' : 'bg-slate-50'
                        } rounded-xl flex items-center justify-center shadow-inner relative`}>
                          <Icon className={`${
                            comp.mode === COMPETITION_MODES.TUG_OF_WAR ? 'text-red-400' : 'text-slate-400'
                          } w-6 h-6`} />
                          {isParticipant && userPlace <= 3 && (
                            <div className="absolute -top-1 -right-1">
                              <Medal className={`w-5 h-5 ${getMedalColor(userPlace)}`} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-black text-sm text-slate-600 tracking-tight">
                            {comp.title}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                              #{comp.code}
                            </p>
                            <span className="text-slate-300">•</span>
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3 text-slate-400" />
                              <span className="text-[9px] font-bold text-slate-400">
                                {comp.participants.length}
                              </span>
                            </div>
                          </div>
                          {isParticipant && userPlace && (
                            <div className="mt-1 flex items-center gap-2">
                              <span className={`text-[9px] font-bold ${
                                userPlace <= 3 ? 'text-slate-700' : 'text-slate-500'
                              }`}>
                                {userPlace}. plass
                              </span>
                              <span className="text-slate-300">•</span>
                              <span className="text-[9px] font-bold text-slate-500">
                                {userKm.toFixed(1)} km
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      {isParticipant && userPlace <= 3 && (
                        <Medal className={`w-8 h-8 ${getMedalColor(userPlace)}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Detail view
  const isParticipant = selectedComp.participants.includes(userId);
  const isTugOfWar = selectedComp.mode === COMPETITION_MODES.TUG_OF_WAR;
  const isCreator = selectedComp.creatorId === userId;
  const isFinished = isCompetitionFinished(selectedComp);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBackToList}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h2 className="font-black text-lg text-slate-800 tracking-tight">
              {selectedComp.title}
            </h2>
            <p className="text-[9px] font-black text-orange-400 uppercase tracking-tighter">
              #{selectedComp.code}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {isParticipant && (
            <button
              onClick={onSync}
              disabled={syncing}
              className={`p-2 bg-orange-600 text-white rounded-xl shadow-md hover:bg-orange-700 transition-all ${
                syncing ? 'animate-spin' : ''
              }`}
              title="Synkroniser aktiviteter"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          )}
          
          {isCreator ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 hover:bg-red-50 rounded-xl transition-colors text-red-500"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          ) : isParticipant && (
            <button
              onClick={() => setShowLeaveConfirm(true)}
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-slate-600 text-xs font-bold"
            >
              Forlat
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-black text-lg text-slate-800 mb-2">
              Slett konkurranse?
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Er du sikker på at du vil slette denne konkuransen? Dette kan ikke angres.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleDeleteCompetition}
                className="flex-1 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-colors"
              >
                Slett
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave confirmation modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="font-black text-lg text-slate-800 mb-2">
              Forlat konkurranse?
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              Er du sikker på at du vil forlate denne konkuransen?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowLeaveConfirm(false)}
                className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={handleLeaveCompetition}
                className="flex-1 py-3 bg-orange-600 text-white font-bold rounded-xl hover:bg-orange-700 transition-colors"
              >
                Forlat
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedComp.description && (
        <p className="text-sm text-slate-600 px-1">
          {selectedComp.description}
        </p>
      )}

      {/* Competition finished banner */}
      {isFinished && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-4 rounded-2xl border-2 border-orange-200">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-orange-600" />
            <div>
              <p className="font-black text-sm text-slate-800">
                Konkurransen er avsluttet!
              </p>
              <p className="text-xs text-slate-600">
                Se resultatene nedenfor
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Competition dates for LONGEST_DISTANCE */}
      {selectedComp.mode === COMPETITION_MODES.LONGEST_DISTANCE && selectedComp.endDate && (
        <div className="bg-orange-50 p-4 rounded-2xl border border-orange-100">
          <div className="flex items-center justify-between text-sm">
            <div>
              <p className="text-[9px] font-black uppercase tracking-widest text-orange-400 mb-1">
                Starter
              </p>
              <p className="font-bold text-slate-800">
                {new Date(selectedComp.startDate).toLocaleDateString('nb-NO', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] font-black uppercase tracking-widest text-orange-400 mb-1">
                Slutter
              </p>
              <p className="font-bold text-slate-800">
                {new Date(selectedComp.endDate).toLocaleDateString('nb-NO', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>
          </div>
          {new Date(selectedComp.endDate) < new Date() && (
            <p className="text-xs font-bold text-red-600 mt-2 text-center">
              Konkuransen er avsluttet
            </p>
          )}
        </div>
      )}

      {/* Tug of War Visualization */}
      {isTugOfWar && (
        <TugOfWarVisualization 
          competition={selectedComp}
          leaderboard={leaderboard}
          userId={userId}
        />
      )}

      {/* Leaderboard */}
      <Leaderboard 
        competition={selectedComp}
        leaderboard={leaderboard}
        userId={userId}
        isFinished={isFinished}
      />

      {/* Chat */}
      {isParticipant && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-orange-600" />
            <h3 className="font-black text-[10px] uppercase tracking-widest text-slate-600">
              Chat
            </h3>
          </div>

          <div className="h-64 overflow-y-auto p-4 space-y-3">
            {messages.map(msg => (
              <div
                key={msg.id}
                className={`flex gap-2 ${msg.senderId === userId ? 'flex-row-reverse' : ''}`}
              >
                <div className="text-2xl">{msg.avatar}</div>
                <div className={`flex-1 ${msg.senderId === userId ? 'text-right' : ''}`}>
                  <p className="text-[9px] font-bold text-slate-400">
                    {msg.senderName}
                  </p>
                  <div className={`inline-block relative group ${
                    msg.senderId === userId ? 'text-right' : ''
                  }`}>
                    <div className={`p-3 rounded-2xl ${
                      msg.senderId === userId
                        ? 'bg-orange-600 text-white'
                        : 'bg-slate-100 text-slate-800'
                    }`}>
                      <p className="text-sm font-medium">{msg.text}</p>
                    </div>
                    {msg.senderId === userId && (
                      <button
                        onClick={() => handleDeleteMessage(msg.id)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600"
                        title="Slett melding"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-100 flex gap-2">
            <input
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Skriv en melding..."
              className="flex-1 p-3 bg-slate-50 rounded-xl border-none outline-none text-sm"
            />
            <button
              type="submit"
              disabled={!chatMessage.trim()}
              className="p-3 bg-orange-600 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

// Leaderboard component
const Leaderboard = ({ competition, leaderboard, userId, isFinished = false }) => {
  const isTugOfWar = competition.mode === COMPETITION_MODES.TUG_OF_WAR;
  
  // Bruk arkiverte resultater hvis konkurransen er ferdig og arkivert
  let participants;
  if (competition.archived && competition.finalResults) {
    participants = competition.finalResults.map(result => ({
      id: result.userId,
      name: result.name,
      avatar: result.avatar,
      displayKm: result.totalKm,
      team: competition.teams?.[result.userId]
    }));
  } else {
    // Live data
    participants = competition.participants
      .map(id => {
        const participant = leaderboard.find(l => l.id === id);
        if (!participant) return null;
        
        // Bruk alltid competitionKm hvis tilgjengelig, ellers 0 (ikke synket enda)
        let displayKm = participant.competitionKm?.[competition.id] ?? 0;
        
        return {
          ...participant,
          displayKm,
          team: competition.teams?.[id]
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.displayKm - a.displayKm);
  }

  // Medal helper
  const getMedalColor = (index) => {
    if (index === 0) return 'text-yellow-500';
    if (index === 1) return 'text-slate-400';
    if (index === 2) return 'text-orange-600';
    return null;
  };

  const getMedalBg = (index) => {
    if (index === 0) return 'bg-yellow-50 border-yellow-200';
    if (index === 1) return 'bg-slate-50 border-slate-200';
    if (index === 2) return 'bg-orange-50 border-orange-200';
    return 'bg-slate-50 border-slate-100';
  };

  // For tug of war, split into teams
  if (isTugOfWar) {
    const teamAParticipants = participants
      .filter(p => p.team === 'A')
      .sort((a, b) => b.displayKm - a.displayKm);
    
    const teamBParticipants = participants
      .filter(p => p.team === 'B')
      .sort((a, b) => b.displayKm - a.displayKm);

    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-black text-[10px] uppercase tracking-widest text-slate-600">
            Leaderboard
          </h3>
        </div>

        <div className="grid grid-cols-2 divide-x divide-slate-100">
          {/* Lag A */}
          <div>
            <div className="p-3 bg-blue-50 border-b border-blue-100">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wider text-center">
                Lag A
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {teamAParticipants.map((participant, index) => (
                <div
                  key={participant.id}
                  className={`p-3 ${
                    participant.id === userId ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-[10px] ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-slate-200 text-slate-600' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-50 text-slate-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="text-xl">{participant.avatar}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs text-slate-800 truncate">
                        {participant.name}
                      </p>
                    </div>
                  </div>
                  <p className="font-black text-sm text-slate-800 tabular-nums text-right">
                    {participant.displayKm.toFixed(1)}
                    <span className="text-[10px] text-slate-400 ml-1">km</span>
                  </p>
                </div>
              ))}
              {teamAParticipants.length === 0 && (
                <div className="p-6 text-center text-slate-400 text-xs">
                  Ingen deltakere ennå
                </div>
              )}
            </div>
          </div>

          {/* Lag B */}
          <div>
            <div className="p-3 bg-red-50 border-b border-red-100">
              <p className="text-xs font-bold text-red-600 uppercase tracking-wider text-center">
                Lag B
              </p>
            </div>
            <div className="divide-y divide-slate-100">
              {teamBParticipants.map((participant, index) => (
                <div
                  key={participant.id}
                  className={`p-3 ${
                    participant.id === userId ? 'bg-red-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center font-black text-[10px] ${
                      index === 0 ? 'bg-yellow-100 text-yellow-700' :
                      index === 1 ? 'bg-slate-200 text-slate-600' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-50 text-slate-400'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="text-xl">{participant.avatar}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-xs text-slate-800 truncate">
                        {participant.name}
                      </p>
                    </div>
                  </div>
                  <p className="font-black text-sm text-slate-800 tabular-nums text-right">
                    {participant.displayKm.toFixed(1)}
                    <span className="text-[10px] text-slate-400 ml-1">km</span>
                  </p>
                </div>
              ))}
              {teamBParticipants.length === 0 && (
                <div className="p-6 text-center text-slate-400 text-xs">
                  Ingen deltakere ennå
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Regular leaderboard for non-tug-of-war competitions
  const isFirstToGoal = competition.mode === COMPETITION_MODES.FIRST_TO_GOAL;
  const goal = competition.goal || 0;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100">
        <h3 className="font-black text-[10px] uppercase tracking-widest text-slate-600">
          Leaderboard
        </h3>
      </div>

      {/* Goal Banner for First to Goal */}
      {isFirstToGoal && goal > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-4 border-b border-orange-100">
          <div className="text-center">
            <p className="text-xs font-bold text-orange-600 uppercase tracking-wider mb-1">
              Mål
            </p>
            <p className="text-3xl font-black text-slate-800">
              {goal} <span className="text-lg text-slate-500">km</span>
            </p>
            <p className="text-xs text-slate-600 mt-1">
              Bli førstemann til å nå målet!
            </p>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {participants.map((participant, index) => {
          const medalColor = isFinished ? getMedalColor(index) : null;
          const showMedal = isFinished && index < 3;
          const progress = isFirstToGoal && goal > 0 ? Math.min((participant.displayKm / goal) * 100, 100) : 0;
          const hasReachedGoal = isFirstToGoal && participant.displayKm >= goal;
          
          return (
            <div
              key={participant.id}
              className={`p-4 ${
                participant.id === userId ? 'bg-orange-50' : ''
              } ${isFinished && index < 3 ? 'border-l-4 ' + (
                index === 0 ? 'border-yellow-500' :
                index === 1 ? 'border-slate-400' :
                'border-orange-600'
              ) : ''} ${hasReachedGoal ? 'bg-green-50' : ''}`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs relative ${
                  isFinished && index < 3 ? getMedalBg(index) : 
                  index === 0 ? 'bg-yellow-100 text-yellow-700' :
                  index === 1 ? 'bg-slate-200 text-slate-600' :
                  index === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-slate-50 text-slate-400'
                }`}>
                  {index + 1}
                  {showMedal && (
                    <Medal className={`absolute -top-1 -right-1 w-4 h-4 ${medalColor}`} />
                  )}
                </div>
                <div className="text-2xl">{participant.avatar}</div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-slate-800">
                    {participant.name}
                  </p>
                  {hasReachedGoal && (
                    <p className="text-xs font-bold text-green-600">
                      🎉 Mål nådd!
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-black text-lg text-slate-800 tabular-nums">
                    {participant.displayKm.toFixed(1)}
                    <span className="text-xs text-slate-400 ml-1">km</span>
                  </p>
                  {isFirstToGoal && goal > 0 && (
                    <p className="text-xs font-bold text-orange-600">
                      {progress.toFixed(0)}%
                    </p>
                  )}
                </div>
              </div>
              
              {/* Progress Bar for First to Goal */}
              {isFirstToGoal && goal > 0 && (
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      hasReachedGoal ? 'bg-green-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Tug of War Visualization
const TugOfWarVisualization = ({ competition, leaderboard, userId }) => {
  const goal = competition.goal || 20; // Target KM
  const userTeam = competition.teams?.[userId];
  
  // Bruk arkiverte data hvis tilgjengelig
  let stats;
  if (competition.archived && competition.finalResults) {
    const teamAResults = competition.finalResults.filter(r => r.team === 'A');
    const teamBResults = competition.finalResults.filter(r => r.team === 'B');
    stats = {
      teamA: teamAResults.reduce((sum, r) => sum + r.totalKm, 0),
      teamB: teamBResults.reduce((sum, r) => sum + r.totalKm, 0)
    };
  } else {
    stats = calculateTugStats(competition, leaderboard);
  }
  
  const difference = stats.teamA - stats.teamB; // Netto-differanse
  
  // Beregn posisjon av midtstreken (50% = midt, 0% = helt til venstre, 100% = helt til høyre)
  // Formelen: 50% + (differanse / (mål * 2)) * 100
  const position = 50 + (difference / (goal * 2)) * 100;
  const clampedPosition = Math.max(0, Math.min(100, position));
  
  // Sjekk om noen har vunnet
  const teamAWins = difference >= goal;
  const teamBWins = difference <= -goal;
  const hasWinner = teamAWins || teamBWins;

  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-black text-[10px] uppercase tracking-widest text-slate-600">
          Tauetrekking
        </h3>
        {userTeam && (
          <div className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
            userTeam === 'A' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
          }`}>
            Du er på Lag {userTeam}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {/* Target Goal */}
        <div className="text-center">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
            Mål
          </p>
          <p className="text-2xl font-black text-slate-800">
            {goal} <span className="text-sm text-slate-400">km</span>
          </p>
        </div>

        {/* Winner Banner */}
        {hasWinner && (
          <div className={`p-4 rounded-2xl border-2 ${
            teamAWins ? 'bg-blue-50 border-blue-300' : 'bg-red-50 border-red-300'
          }`}>
            <p className="text-center font-black text-lg">
              🎉 Lag {teamAWins ? 'A' : 'B'} vinner!
            </p>
          </div>
        )}

        {/* Visual bar - Ekte tautrekking */}
        <div className="relative h-12 rounded-full overflow-hidden">
          {/* Lag A (blå) fyller fra venstre */}
          <div
            className="absolute left-0 top-0 h-full bg-blue-500 transition-all duration-500"
            style={{ width: `${clampedPosition}%` }}
          />
          {/* Lag B (rød) fyller fra høyre */}
          <div
            className="absolute right-0 top-0 h-full bg-red-500 transition-all duration-500"
            style={{ width: `${100 - clampedPosition}%` }}
          />
          {/* Midtstreken */}
          <div 
            className="absolute top-0 h-full w-1 bg-slate-800 transition-all duration-500 shadow-lg"
            style={{ left: `${clampedPosition}%`, transform: 'translateX(-50%)' }}
          />
        </div>

        {/* Stats */}
        <div className="flex justify-between">
          <div className="text-center">
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">
              Lag A
            </p>
            <p className="text-2xl font-black text-slate-800 tabular-nums">
              {stats.teamA.toFixed(1)}
              <span className="text-sm text-slate-400 ml-1">km</span>
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-1">
              Lag B
            </p>
            <p className="text-2xl font-black text-slate-800 tabular-nums">
              {stats.teamB.toFixed(1)}
              <span className="text-sm text-slate-400 ml-1">km</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
